import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Modal, Button, Alert } from "react-bootstrap";
import {
  createAddress,
  updateAddress,
  checkDuplicate,
  setDuplicateWarning,
} from "../../redux/features/addressSlice";
import { handleCookies } from "../../utils/cookies";
import { handleCompanyDetails } from "../../services/companies";
import "./AddressForm.css";

const validationSchema = Yup.object().shape({
  policy_id: Yup.number().required("Policy is required"),
  address_line_1: Yup.string().required("Address line 1 is required"),
  address_line_2: Yup.string(),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zip: Yup.string().required("Zip code is required"),
  country: Yup.string(),
});

function AddressForm({ policyId, address, onClose, onSuccess, isAllAddresses = false }) {
  const dispatch = useDispatch();
  const [submitError, setSubmitError] = useState("");
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [proceedWithDuplicate, setProceedWithDuplicate] = useState(false);
  const [policies, setPolicies] = useState([]);
  const [loadingPolicies, setLoadingPolicies] = useState(false);
  const currentUserData = handleCookies.fetchCookies();

  useEffect(() => {
    if (isAllAddresses) {
      fetchAccessiblePolicies();
    }
  }, [isAllAddresses]);

  const fetchAccessiblePolicies = async () => {
    setLoadingPolicies(true);
    try {
      // Fetch all policies the user can access
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/policies`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${handleCookies.fetchCookies().token}`,
        },
        body: JSON.stringify({
          offset: 0,
          limit: 1000,
        }),
      });
      const data = await response.json();
      if (data.data && data.data.data) {
        setPolicies(data.data.data);
      }
    } catch (error) {
      console.error("Error fetching policies:", error);
    } finally {
      setLoadingPolicies(false);
    }
  };

  const initialValues = address
    ? {
        policy_id: address.policy_id || policyId || "",
        address_line_1: address.address_line_1 || "",
        address_line_2: address.address_line_2 || "",
        city: address.city || "",
        state: address.state || "",
        zip: address.zip || "",
        country: address.country || "USA",
      }
    : {
        policy_id: policyId || "",
        address_line_1: "",
        address_line_2: "",
        city: "",
        state: "",
        zip: "",
        country: "USA",
      };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setSubmitError("");
      const selectedPolicyId = values.policy_id;

      // Check for duplicates only on create
      if (!address && !proceedWithDuplicate) {
        try {
          const duplicateResult = await dispatch(
            checkDuplicate({ policyId: selectedPolicyId, data: values })
          ).unwrap();

          if (duplicateResult.is_duplicate) {
            setDuplicateWarning(true);
            setSubmitting(false);
            return;
          }
        } catch (error) {
          console.error("Error checking duplicate:", error);
          // Continue with submission even if duplicate check fails
        }
      }

      if (address) {
        await dispatch(
          updateAddress({ policyId: selectedPolicyId, addressId: address.id, data: values })
        ).unwrap();
      } else {
      const res = await dispatch(
          createAddress({ policyId: selectedPolicyId, data: values })
        ).unwrap();
        onSuccess(res.id);
      }
    } catch (error) {
      console.error("Error saving address:", error);
      setSubmitError(
        error.message || "Failed to save address. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleProceedWithDuplicate = () => {
    setProceedWithDuplicate(true);
    setDuplicateWarning(false);
  };

  return (
    <>
      <Modal show={true} onHide={onClose} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {address ? "Edit Address" : "Add New Address"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitError && (
            <Alert
              variant="danger"
              onClose={() => setSubmitError("")}
              dismissible
            >
              {submitError}
            </Alert>
          )}
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ isSubmitting, isValid }) => (
              <Form>
                {isAllAddresses && (
                  <div className="form-group">
                    <label htmlFor="policy_id">Policy *</label>
                    <Field
                      as="select"
                      id="policy_id"
                      name="policy_id"
                      className="form-control"
                      disabled={loadingPolicies}
                    >
                      <option value="">Select a policy</option>
                      {policies.map((policy) => (
                        <option key={policy.id} value={policy.id}>
                          {policy.policy_number} - {policy.policy_holder_name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage
                      name="policy_id"
                      component="div"
                      className="error-message"
                    />
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="address_line_1">Address Line 1 *</label>
                  <Field
                    type="text"
                    id="address_line_1"
                    name="address_line_1"
                    className="form-control"
                    placeholder="Enter address line 1"
                  />
                  <ErrorMessage
                    name="address_line_1"
                    component="div"
                    className="error-message"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="address_line_2">Address Line 2</label>
                  <Field
                    type="text"
                    id="address_line_2"
                    name="address_line_2"
                    className="form-control"
                    placeholder="Enter address line 2 (optional)"
                  />
                  <ErrorMessage
                    name="address_line_2"
                    component="div"
                    className="error-message"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <Field
                      type="text"
                      id="city"
                      name="city"
                      className="form-control"
                      placeholder="Enter city"
                    />
                    <ErrorMessage
                      name="city"
                      component="div"
                      className="error-message"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">State *</label>
                    <Field
                      type="text"
                      id="state"
                      name="state"
                      className="form-control"
                      placeholder="Enter state"
                    />
                    <ErrorMessage
                      name="state"
                      component="div"
                      className="error-message"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="zip">Zip Code *</label>
                    <Field
                      type="text"
                      id="zip"
                      name="zip"
                      className="form-control"
                      placeholder="Enter zip code"
                    />
                    <ErrorMessage
                      name="zip"
                      component="div"
                      className="error-message"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="country">Country</label>
                  <Field
                    type="text"
                    id="country"
                    name="country"
                    className="form-control"
                    placeholder="Enter country"
                  />
                  <ErrorMessage
                    name="country"
                    component="div"
                    className="error-message"
                  />
                </div>

                <Modal.Footer>
                  <Button variant="secondary" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting || !isValid}
                  >
                    {isSubmitting ? "Saving..." : "Save Address"}
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>

      {duplicateWarning && (
        <Modal show={true} onHide={() => setDuplicateWarning(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>Duplicate Address Warning</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              An address with similar details already exists for this policy.
              Do you want to proceed anyway?
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setDuplicateWarning(false)}
            >
              Cancel
            </Button>
            <Button
              variant="warning"
              onClick={handleProceedWithDuplicate}
            >
              Proceed Anyway
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </>
  );
}

export default AddressForm;
